import { useEffect, useRef, useState } from "react";
import { Upload, FileText, Save, Eye, EyeOff, Loader2, Download } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { formatDateTime } from "../lib/adminConstants";

const ALL_ROUTES = [
    { value: "/", label: "Home" },
    { value: "/unternehmen", label: "Für Unternehmen" },
    { value: "/kandidaten", label: "Für Kandidaten" },
    { value: "/ueber-uns", label: "Über uns" },
    { value: "/kontakt", label: "Kontakt" },
];

const DEFAULT_CFG = {
    enabled: true,
    display_routes: ["/", "/unternehmen"],
    eyebrow: "Kostenloser Guide",
    title: "Recruiting-Playbook 2026",
    description: "42 Seiten Insights. Wie Sie Top-Talente finden, gewinnen und halten — datengetrieben und human.",
    bullets: [
        "Sourcing-Strategien für 2026",
        "Gehälter-Benchmarks DACH",
        "Interview-Frameworks die wirklich funktionieren",
    ],
    form_title: "Jetzt downloaden",
    form_cta: "Guide anfordern",
    form_disclaimer: "DSGVO-konform. Jederzeit abbestellbar.",
    success_title: "Check Ihre Inbox",
    success_text: "Der Guide ist auf dem Weg.",
    mail_subject: "Ihr Recruiting-Playbook 2026 von aimploy",
    mail_body_html:
        "<p>Hallo {name},</p><p>vielen Dank für Ihr Interesse am <strong>Recruiting-Playbook 2026</strong>.</p><p>Im Anhang finden Sie das vollständige PDF mit 42 Seiten Insights, Benchmarks und Frameworks.</p><p>Falls Sie Fragen haben oder eine konkrete Rolle besetzen möchten, antworten Sie einfach auf diese Mail — wir melden uns persönlich.</p><p>Beste Grüße<br/>Ihr aimploy Team</p>",
    pdf_filename: "aimploy-recruiting-playbook-2026.pdf",
    pdf_path: "",
    pdf_size: 0,
    pdf_uploaded_at: "",
};

export default function LeadMagnetEditor() {
    const [cfg, setCfg] = useState(DEFAULT_CFG);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [bulletsText, setBulletsText] = useState(DEFAULT_CFG.bullets.join("\n"));
    const fileRef = useRef(null);

    const load = async () => {
        try {
            const r = await api.get("/admin/lead-magnet");
            const merged = { ...DEFAULT_CFG, ...r.data };
            setCfg(merged);
            setBulletsText((merged.bullets || []).join("\n"));
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        }
    };

    useEffect(() => {
        load();
    }, []);

    const update = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

    const toggleRoute = (r) => {
        setCfg((c) => {
            const cur = new Set(c.display_routes || []);
            if (cur.has(r)) cur.delete(r); else cur.add(r);
            return { ...c, display_routes: Array.from(cur) };
        });
    };

    const save = async () => {
        setSaving(true);
        try {
            const bullets = bulletsText
                .split("\n")
                .map((s) => s.trim())
                .filter(Boolean);
            const payload = { ...cfg, bullets };
            // strip server-managed fields
            delete payload.updated_at;
            delete payload.updated_by;
            delete payload.pdf_uploaded_at;
            delete payload.pdf_size;
            delete payload.pdf_path;
            await api.put("/admin/lead-magnet", payload);
            toast.success("Konfiguration gespeichert");
            await load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const uploadPdf = async (file) => {
        if (!file) return;
        if (file.type !== "application/pdf") {
            toast.error("Nur PDF-Dateien erlaubt");
            return;
        }
        setUploading(true);
        const fd = new FormData();
        fd.append("pdf", file);
        try {
            await api.post("/admin/lead-magnet/pdf", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("PDF hochgeladen");
            await load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    return (
        <div data-testid="admin-lead-magnet-view">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
                <h1 className="font-display text-4xl lg:text-5xl font-bold">Lead-Magnet</h1>
                <button
                    onClick={save}
                    disabled={saving}
                    data-testid="lead-magnet-save"
                    className="inline-flex items-center gap-2 bg-aimploy-navy hover:bg-aimploy-navy-hover disabled:opacity-60 text-white px-5 py-2.5 rounded-full text-sm font-semibold transition-colors"
                >
                    <Save className="w-4 h-4" /> {saving ? "Speichere..." : "Speichern"}
                </button>
            </div>
            <p className="text-aimploy-muted mb-8">PDF, Texte und E-Mail-Versand zentral verwalten.</p>

            {/* Visibility */}
            <div className="bg-white rounded-2xl p-6 lg:p-8 border border-black/5 mb-5">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-display text-xl font-bold">Sichtbarkeit</h3>
                    <button
                        onClick={() => update("enabled", !cfg.enabled)}
                        data-testid="lead-magnet-toggle-enabled"
                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold transition-colors ${
                            cfg.enabled ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-500"
                        }`}
                    >
                        {cfg.enabled ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                        {cfg.enabled ? "Aktiv" : "Deaktiviert"}
                    </button>
                </div>
                <div className="text-xs uppercase tracking-[0.15em] text-aimploy-muted font-semibold mb-3">
                    Auf welchen Seiten anzeigen?
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {ALL_ROUTES.map((r) => {
                        const active = (cfg.display_routes || []).includes(r.value);
                        return (
                            <button
                                key={r.value}
                                onClick={() => toggleRoute(r.value)}
                                data-testid={`lead-magnet-route-${r.value === "/" ? "home" : r.value.slice(1)}`}
                                className={`text-left px-4 py-3 rounded-xl border-2 text-sm font-medium transition-all ${
                                    active
                                        ? "border-aimploy-violet bg-aimploy-violet/5 text-aimploy-navy"
                                        : "border-black/5 bg-aimploy-bg text-aimploy-muted hover:border-black/15"
                                }`}
                            >
                                {r.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* PDF */}
            <div className="bg-white rounded-2xl p-6 lg:p-8 border border-black/5 mb-5">
                <h3 className="font-display text-xl font-bold mb-4">PDF</h3>
                {cfg.pdf_path ? (
                    <div className="flex items-start gap-4 bg-aimploy-bg rounded-xl p-4">
                        <div className="h-10 w-10 rounded-lg bg-aimploy-violet/10 text-aimploy-violet flex items-center justify-center flex-shrink-0">
                            <FileText className="w-5 h-5" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="font-semibold truncate">{cfg.pdf_filename}</div>
                            <div className="text-xs text-aimploy-muted">
                                {(cfg.pdf_size / 1024 / 1024).toFixed(2)} MB · hochgeladen {formatDateTime(cfg.pdf_uploaded_at)}
                            </div>
                        </div>
                        <a
                            href="/api/admin/lead-magnet/pdf"
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs text-aimploy-violet font-semibold inline-flex items-center gap-1 flex-shrink-0"
                        >
                            <Download className="w-3.5 h-3.5" /> Vorschau
                        </a>
                    </div>
                ) : (
                    <div className="bg-amber-50 text-amber-800 rounded-xl p-4 text-sm mb-4">
                        Noch kein PDF hochgeladen. Ohne PDF werden keine Mails versendet.
                    </div>
                )}
                <div className="mt-4 flex flex-wrap items-center gap-3">
                    <input
                        ref={fileRef}
                        type="file"
                        accept="application/pdf"
                        onChange={(e) => uploadPdf(e.target.files?.[0])}
                        data-testid="lead-magnet-pdf-input"
                        className="hidden"
                        id="lm-pdf-input"
                    />
                    <label
                        htmlFor="lm-pdf-input"
                        data-testid="lead-magnet-pdf-upload"
                        className={`inline-flex items-center gap-2 cursor-pointer ${
                            uploading ? "opacity-60 pointer-events-none" : ""
                        } bg-aimploy-violet hover:bg-aimploy-violet-hover text-white px-5 py-2.5 rounded-full text-sm font-semibold transition-colors`}
                    >
                        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                        {cfg.pdf_path ? "PDF austauschen" : "PDF hochladen"}
                    </label>
                    <input
                        type="text"
                        value={cfg.pdf_filename || ""}
                        onChange={(e) => update("pdf_filename", e.target.value)}
                        placeholder="Anhang-Dateiname"
                        data-testid="lead-magnet-pdf-filename"
                        className="border border-black/10 rounded-xl px-4 py-2 text-sm flex-1 min-w-[200px]"
                    />
                </div>
            </div>

            {/* Section text */}
            <div className="bg-white rounded-2xl p-6 lg:p-8 border border-black/5 mb-5">
                <h3 className="font-display text-xl font-bold mb-4">Sektion auf der Website</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label="Eyebrow (kleiner Text oben)" value={cfg.eyebrow} onChange={(v) => update("eyebrow", v)} testid="lm-eyebrow" />
                    <Field label="Titel" value={cfg.title} onChange={(v) => update("title", v)} testid="lm-title" />
                    <Field label="Form-Titel (rechts)" value={cfg.form_title} onChange={(v) => update("form_title", v)} testid="lm-form-title" />
                    <Field label="Button-Text" value={cfg.form_cta} onChange={(v) => update("form_cta", v)} testid="lm-form-cta" />
                </div>
                <Field label="Beschreibung" value={cfg.description} onChange={(v) => update("description", v)} multiline testid="lm-description" />
                <Field
                    label="Bullets (eine Zeile = ein Bullet)"
                    value={bulletsText}
                    onChange={setBulletsText}
                    multiline
                    rows={4}
                    testid="lm-bullets"
                />
                <Field label="Disclaimer unter dem Button" value={cfg.form_disclaimer} onChange={(v) => update("form_disclaimer", v)} testid="lm-disclaimer" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label="Erfolg-Titel" value={cfg.success_title} onChange={(v) => update("success_title", v)} testid="lm-success-title" />
                    <Field label="Erfolg-Text" value={cfg.success_text} onChange={(v) => update("success_text", v)} testid="lm-success-text" />
                </div>
            </div>

            {/* Email */}
            <div className="bg-white rounded-2xl p-6 lg:p-8 border border-black/5">
                <h3 className="font-display text-xl font-bold mb-1">E-Mail</h3>
                <p className="text-xs text-aimploy-muted mb-4">
                    Platzhalter: <code className="bg-aimploy-bg px-1 rounded">{`{name}`}</code> wird durch den Namen des Leads ersetzt,{" "}
                    <code className="bg-aimploy-bg px-1 rounded">{`{email}`}</code> durch die E-Mail.
                </p>
                <Field label="Betreff" value={cfg.mail_subject} onChange={(v) => update("mail_subject", v)} testid="lm-mail-subject" />
                <Field
                    label="HTML-Inhalt der Mail"
                    value={cfg.mail_body_html}
                    onChange={(v) => update("mail_body_html", v)}
                    multiline
                    rows={10}
                    testid="lm-mail-body"
                    mono
                />
            </div>
        </div>
    );
}

function Field({ label, value, onChange, multiline, rows = 3, testid, mono }) {
    return (
        <label className="block mt-4 first:mt-0">
            <div className="text-xs uppercase tracking-[0.15em] text-aimploy-muted font-semibold mb-2">{label}</div>
            {multiline ? (
                <textarea
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    rows={rows}
                    data-testid={testid}
                    className={`w-full border border-black/10 rounded-xl px-4 py-3 text-sm resize-y ${mono ? "font-mono" : ""}`}
                />
            ) : (
                <input
                    type="text"
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    data-testid={testid}
                    className="w-full border border-black/10 rounded-xl px-4 py-3 text-sm"
                />
            )}
        </label>
    );
}
